// Клиент API по таблице маршрутов @kp/contracts (contracts/api.md, «Общие правила»):
// - изменяющие запросы (POST, PATCH) идут с X-KP-Request: 1 — иначе сервер отвечает SYS-002 (FR-012а);
// - фоновые запросы (опросы, перезапросы при возврате связи и вкладки) — с X-KP-Background: 1,
//   они не продлевают сеанс (FR-008б);
// - изменение записи — с If-Match: <version>;
// - ошибка — ApiError с кодом, причиной и следующим шагом из ответа сервера, а без тела — по
//   каталогу ошибок; номер операции — из поля operation, а нет его — из заголовка X-KP-Operation
//   (FR-027, FR-028); AUTH-004 (сеанс закончился) — событие повторного входа с повтором запроса,
//   кроме выхода: выходить уже не из чего, окна входа нет (FR-009, FR-011а);
// - номер операции последнего ответа маршрута, в том числе успешного, — lastOperation: экран
//   «Нет доступа» пользователя без ролей строится по ответу auth.me, а не по отказу (T058);
// - нет связи — NetworkError и событие «связь потеряна»;
// - Referer — только адрес сервиса (referrerPolicy: 'origin'), Origin — настоящий;
// - загрузка файла протокола (003) — upload(): тело — файл .xlsx как есть, имя файла —
//   в заголовке X-KP-File-Name (encodeURIComponent), без multipart.
import {
  ApiErrorSchema,
  FILE_NAME_HEADER,
  OPERATION_HEADER,
  OperationIdSchema,
  XLSX_CONTENT_TYPE,
  buildPath,
  isErrorCode,
  isMutating,
  renderError,
  routes,
} from '@kp/contracts';
import type {
  ApiError as ApiErrorBody,
  ErrorCode,
  RenderedError,
  RouteBody,
  RouteDefinition,
  RouteName,
  RouteParams,
  RouteQuery,
  RouteResponse,
  Routes,
} from '@kp/contracts';

export const REQUEST_HEADER_NAME = 'X-KP-Request';
export const BACKGROUND_HEADER_NAME = 'X-KP-Background';

interface RequestOptions {
  signal?: AbortSignal;
  /**
   * Фоновый запрос: опрос уведомлений. Не продлевает сеанс. Не указан — фоновый, если начат
   * внутри inBackground или продолжает его перечитывание (следующая страница списка).
   */
  background?: boolean;
  /** Запрос переживает страницу: отложенное удаление при закрытии вкладки (T121). */
  keepalive?: boolean;
}

/**
 * Версия записи для If-Match: обязательна, если маршрут требует заголовок (правка записи), и по
 * желанию, если заголовок необязателен (снятие и возврат роли).
 */
type VersionInput<N extends RouteName> = Routes[N] extends {
  readonly headers: { readonly _zod: { readonly output: infer H } };
}
  ? H extends { 'if-match': number }
    ? { version: number }
    : { version?: number }
  : unknown;

/** Что передаёт вызывающий: параметры пути, строка запроса, тело, версия записи. */
export type ApiInput<N extends RouteName> = (RouteParams<N> extends undefined
  ? unknown
  : { params: RouteParams<N> }) &
  (RouteQuery<N> extends undefined ? unknown : { query?: RouteQuery<N> }) &
  (RouteBody<N> extends undefined ? unknown : { body: RouteBody<N> }) &
  VersionInput<N> &
  RequestOptions;

/** Вход необязателен, если у маршрута нет обязательных частей. */
export type ApiArgs<N extends RouteName> =
  Record<never, never> extends ApiInput<N> ? [input?: ApiInput<N>] : [input: ApiInput<N>];

/** Ответ сервера с кодом из каталога. */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly code: ErrorCode;
  readonly status: number;
  readonly nextStep: string;
  readonly fields: Readonly<Record<string, string>>;
  /** Тело ответа целиком: retryAfter, count, existingUserId, current, lastChange. */
  readonly body: ApiErrorBody;
  readonly route: RouteName;
  /**
   * Номер операции (FR-027): из поля operation, а нет его — из заголовка X-KP-Operation.
   * Нет у ответа не от сервиса (прокси).
   */
  readonly operation: string | undefined;

  constructor(route: RouteName, status: number, body: ApiErrorBody, operation?: string) {
    super(body.message);
    this.route = route;
    this.status = status;
    this.code = body.code;
    this.nextStep = body.nextStep;
    this.fields = body.fields ?? {};
    this.body = body;
    this.operation = body.operation ?? operation;
  }
}

/**
 * Запрос не дошёл до сервера: «Нет связи. Изменения сохранятся, когда связь вернётся». Номера
 * операции нет — сервер запроса не видел (contracts/errors.md 002).
 */
export class NetworkError extends Error {
  override readonly name = 'NetworkError';
  readonly route: RouteName;

  constructor(route: RouteName, cause: unknown) {
    super('Нет связи. Изменения сохранятся, когда связь вернётся', { cause });
    this.route = route;
  }
}

export function isApiError(error: unknown, code?: ErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

// --- События: повторный вход и связь ---

/** Сеанс закончился (AUTH-004): окно «Войдите снова» и повтор последнего действия. */
export interface ReauthRequest {
  route: RouteName;
  background: boolean;
  error: ApiError;
  /** Повторяет тот же запрос после входа. */
  retry: () => Promise<unknown>;
}

const events = new EventTarget();
const REAUTH = 'reauth-required';
const CONNECTION = 'connection';
let online = true;

/**
 * Обработчик повторного входа (окно «Войдите снова», T045): берёт запрос, получивший AUTH-004,
 * и возвращает обещание его повтора после входа — вызывающий дождётся результата, будто
 * сеанс не заканчивался, и введённое не теряется. null — обработчик не берётся, запрос
 * завершается ошибкой AUTH-004.
 */
export type ReauthHandler = (request: ReauthRequest) => Promise<unknown> | null;

let reauthHandler: ReauthHandler | null = null;

/** Регистрирует обработчик повторного входа; возвращает отмену регистрации. */
export function setReauthHandler(handler: ReauthHandler): () => void {
  reauthHandler = handler;
  return () => {
    if (reauthHandler === handler) reauthHandler = null;
  };
}

/** Подписка на событие повторного входа; возвращает отписку. */
export function onReauthRequired(listener: (request: ReauthRequest) => void): () => void {
  const handler = (event: Event) => listener((event as CustomEvent<ReauthRequest>).detail);
  events.addEventListener(REAUTH, handler);
  return () => events.removeEventListener(REAUTH, handler);
}

/** Есть ли связь с сервером по последнему запросу. */
export function isOnline(): boolean {
  return online;
}

/** Подписка на потерю и возврат связи; возвращает отписку. */
export function onConnectionChange(listener: (online: boolean) => void): () => void {
  const handler = (event: Event) => listener((event as CustomEvent<boolean>).detail);
  events.addEventListener(CONNECTION, handler);
  return () => events.removeEventListener(CONNECTION, handler);
}

function setOnline(value: boolean): void {
  if (online === value) return;
  online = value;
  events.dispatchEvent(new CustomEvent(CONNECTION, { detail: value }));
}

// --- Фоновые запросы ---

let backgroundDepth = 0;

/** Сигналы отмены фоновых перечитываний: следующие страницы того же перечитывания — фоновые. */
const backgroundSignals = new WeakSet<AbortSignal>();

/**
 * Запросы чтения, начатые внутри run, — фоновые (X-KP-Background: 1): сеанс они не продлевают
 * (FR-008б). Так помечаются перезапросы, которые никто не просил: данные экрана при возврате
 * связи и вкладки, опрос «Оповещений». Отметка — у самого перечитывания, а не у времени, пока
 * оно идёт. Списки с подгрузкой перечитывают страницы одну за другой с тем же сигналом отмены
 * TanStack — следующие страницы тоже фоновые (T151). «Показать ещё», переход и изменение,
 * которые человек сделал в это время, — его действия: обычные запросы, сеанс продлевается (T123).
 */
export function inBackground<T>(run: () => T): T {
  backgroundDepth += 1;
  try {
    return run();
  } finally {
    backgroundDepth -= 1;
  }
}

/**
 * «Выйти», когда сеанс уже закончился (AUTH-004): выходить не из чего — этот сеанс завершён.
 * Окно «Войдите снова» не открывается, запрос завершается без ошибки, и вызывающий, как после
 * обычного выхода, стирает данные из памяти страницы и ведёт на вход (FR-009, FR-011а).
 * «Выйти на всех устройствах» сюда не входит: другие сеансы ещё действуют, поэтому открывается
 * «Войдите снова», и после входа действие повторяется и завершает их (FR-009).
 */
const sessionEndRoutes: ReadonlySet<RouteName> = new Set(['auth.logout']);

// --- Запрос ---

function queryString(query: unknown): string {
  if (query === null || typeof query !== 'object') return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    const values: unknown[] = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        search.append(key, String(item));
      }
    }
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** Адрес запроса: путь с параметрами и строка запроса. */
export function routeUrl<N extends RouteName>(name: N, input?: ApiInput<N>): string {
  const parts = (input ?? {}) as RawInput;
  return buildPath(routes[name].path, parts.params) + queryString(parts.query);
}

/** Тексты кода без меток — когда сервер не прислал своих (ответ прокси, обрыв). */
const catalogText = renderError as (code: ErrorCode) => RenderedError;

/** Номер операции из заголовка X-KP-Operation; чужое значение (прокси) не берётся. */
function headerOperation(response: Response): string | undefined {
  const parsed = OperationIdSchema.safeParse(response.headers.get(OPERATION_HEADER));
  return parsed.success ? parsed.data : undefined;
}

const lastOperations = new Map<RouteName, string>();

/**
 * Номер операции последнего ответа сервиса по маршруту, в том числе успешного (заголовок
 * X-KP-Operation). «Нет доступа» у пользователя без ролей показывает номер ответа auth.me:
 * по нему сопровождение находит этот запрос в техническом журнале (FR-028, T058). Ответа ещё
 * не было или он пришёл не от сервиса (прокси) — номера нет.
 */
export function lastOperation(name: RouteName): string | undefined {
  return lastOperations.get(name);
}

async function readError(
  name: RouteName,
  response: Response,
  operation: string | undefined,
): Promise<ApiError> {
  const json: unknown = await response.json().catch(() => null);
  const parsed = ApiErrorSchema.safeParse(json);
  if (parsed.success) return new ApiError(name, response.status, parsed.data, operation);
  // Тело без текстов, но с кодом — тексты из каталога.
  const code =
    json !== null &&
    typeof json === 'object' &&
    'code' in json &&
    typeof json.code === 'string' &&
    isErrorCode(json.code)
      ? json.code
      : 'SYS-001';
  return new ApiError(name, response.status, catalogText(code), operation);
}

interface RawInput {
  params?: Record<string, string>;
  query?: unknown;
  body?: unknown;
  /** Файл .xlsx телом запроса (upload). */
  file?: File;
  version?: number;
  signal?: AbortSignal;
  background?: boolean;
  keepalive?: boolean;
}

async function send(name: RouteName, input: RawInput = {}): Promise<unknown> {
  const route: RouteDefinition = routes[name];
  // Читается до первого await: inBackground действует на запросы чтения, начатые внутри run,
  // а сигнал отмены такого запроса помечает и следующие страницы того же перечитывания.
  // Изменение всегда обычное: его отправил человек, сеанс продлевается.
  const mutating = isMutating(route.method);
  const background =
    input.background ??
    (!mutating &&
      (backgroundDepth > 0 || (input.signal !== undefined && backgroundSignals.has(input.signal))));
  if (background && !mutating && input.signal) backgroundSignals.add(input.signal);
  const headers = new Headers({ Accept: 'application/json' });
  if (mutating) headers.set(REQUEST_HEADER_NAME, '1');
  if (background) headers.set(BACKGROUND_HEADER_NAME, '1');
  if (input.version !== undefined) headers.set('If-Match', String(input.version));
  let body: string | Blob | undefined;
  if (input.file !== undefined) {
    headers.set('Content-Type', XLSX_CONTENT_TYPE);
    headers.set(FILE_NAME_HEADER, encodeURIComponent(input.file.name));
    body = input.file;
  } else if (input.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(input.body);
  }

  let response: Response;
  try {
    response = await fetch(buildPath(route.path, input.params) + queryString(input.query), {
      method: route.method,
      headers,
      body,
      credentials: 'same-origin',
      // Страница ссылки-приглашения отдаётся с no-referrer (FR-003д), и после неё документ
      // остаётся тем же: без своей политики запрос ушёл бы с Origin: null и получил SYS-002.
      // 'origin' — в Referer только адрес сервиса, без пути с токеном.
      referrerPolicy: 'origin',
      signal: input.signal,
      ...(input.keepalive ? { keepalive: true } : {}),
    });
  } catch (cause) {
    if (input.signal?.aborted) throw cause;
    setOnline(false);
    throw new NetworkError(name, cause);
  }
  setOnline(true);
  const operation = headerOperation(response);
  if (operation) lastOperations.set(name, operation);
  else lastOperations.delete(name);

  if (!response.ok) {
    const error = await readError(name, response, operation);
    if (error.code === 'AUTH-004' && sessionEndRoutes.has(name)) return undefined;
    if (error.code === 'AUTH-004' && route.access === 'session') {
      const request: ReauthRequest = {
        route: name,
        background,
        error,
        // Повтор — тем же запросом, но уже не фоновым: его запускает человек входом.
        retry: () => send(name, { ...input, signal: undefined, background: false }),
      };
      events.dispatchEvent(new CustomEvent(REAUTH, { detail: request }));
      // Фоновый опрос окно входа не открывает: сеанс напомнит о себе первым действием человека.
      const pending = request.background ? null : reauthHandler?.(request);
      if (pending) return pending;
    }
    throw error;
  }

  if (response.status === 204) return undefined;
  if (route.file) return response.arrayBuffer();
  if (!route.response) return undefined;
  return response.json();
}

/**
 * Вызов маршрута по имени из таблицы @kp/contracts:
 * `api('users.update', { params: { id }, version, body: { fullName } })`.
 */
export function api<N extends RouteName>(
  name: N,
  ...[input]: ApiArgs<N>
): Promise<RouteResponse<N>> {
  return send(name, input) as Promise<RouteResponse<N>>;
}

/** Маршруты, тело которых — файл .xlsx (003: предпросмотр, «Новый протокол», «Новая версия»). */
export type UploadRouteName = {
  [N in RouteName]: Routes[N] extends { readonly upload: 'xlsx' } ? N : never;
}[RouteName];

export type UploadInput<N extends UploadRouteName> = (RouteParams<N> extends undefined
  ? unknown
  : { params: RouteParams<N> }) &
  (RouteQuery<N> extends undefined ? unknown : { query?: RouteQuery<N> }) & {
    file: File;
  } & RequestOptions;

/**
 * Загрузка файла протокола: `upload('documents.create', { file, query: { sheet } })`. Файл —
 * телом как есть, имя — в заголовке; ошибки и номер операции — как у api().
 */
export function upload<N extends UploadRouteName>(
  name: N,
  input: UploadInput<N>,
): Promise<RouteResponse<N>> {
  return send(name, input) as Promise<RouteResponse<N>>;
}

/** Ключ кэша TanStack Query для маршрута: ['api', имя, параметры, строка запроса]. */
export function apiKey<N extends RouteName>(name: N, input?: ApiInput<N>): readonly unknown[] {
  const parts = (input ?? {}) as { params?: unknown; query?: unknown };
  return ['api', name, parts.params ?? null, parts.query ?? null];
}

/** Готовые queryKey и queryFn для useQuery: `useQuery(apiQuery('users.list', { query }))`. */
export function apiQuery<N extends RouteName>(
  name: N,
  ...[input]: ApiArgs<N>
): {
  queryKey: readonly unknown[];
  queryFn: (context: { signal: AbortSignal }) => Promise<RouteResponse<N>>;
} {
  const raw = (input ?? {}) as RawInput;
  return {
    queryKey: apiKey(name, input),
    queryFn: ({ signal }) => send(name, { ...raw, signal }) as Promise<RouteResponse<N>>,
  };
}
